import { Helmet, HelmetData } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  AI_BG_NEW,
  Context_Aware_Icon,
  Enterprises_Build,
  Enterprises_Deliver,
  Enterprises_Hero,
  Our_Product_Bg,
  Security_Privacy_Icon,
  Speed_Privacy_Icon,
} from "../../../assets";
import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import {
  MOTION_OK,
  revealTrigger,
  riseFrom,
  maskedLineReveal,
} from "../../../utils/animations";
const helmetData = new HelmetData({});

const why = [
  {
    icon: Security_Privacy_Icon,
    title: "Security & Privacy",
    text: "Keep sensitive data private with secure, fine-tuned models.",
  },
  {
    icon: Speed_Privacy_Icon,
    title: "Speed & Scale",
    text: "Reduce dependence on scarce analyst resources with agentic automation.",
  },
  {
    icon: Context_Aware_Icon,
    title: "Context-Aware Insights",
    text: "Fine-tuned to your industry, your business, your data.",
  },
];

export default function EnterpriseMarketsPage() {
  const pageRef = useRef(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(MOTION_OK, () => {
        maskedLineReveal(pageRef.current.querySelector(".em-hero-heading"), {
          scrollTrigger: revealTrigger(".em-hero-heading"),
        });
        riseFrom(".em-hero-item", {
          delay: 0.2,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".em-hero-item", "top 85%"),
        });
        riseFrom(".em-hero-media", {
          y: 40,
          duration: 1.1,
          delay: 0.3,
          scrollTrigger: revealTrigger(".em-hero-media", "top 85%"),
        });
        riseFrom(".em-what-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".em-what-row", "top 75%"),
        });
        riseFrom(".em-what-copy > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".em-what-row", "top 75%"),
        });
        riseFrom(".em-why-media", {
          y: 40,
          duration: 1.1,
          scrollTrigger: revealTrigger(".em-why-row", "top 75%"),
        });
        riseFrom(".em-why-copy > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".em-why-row", "top 75%"),
        });
        riseFrom(".em-value-head > *", {
          stagger: 0.15,
          scrollTrigger: revealTrigger(".em-value-head"),
        });
        riseFrom(".em-value-card", {
          y: 48,
          duration: 1,
          stagger: 0.12,
          scrollTrigger: revealTrigger(".em-value-grid", "top 75%"),
        });
        riseFrom(".em-cta > *", {
          stagger: 0.12,
          scrollTrigger: revealTrigger(".em-cta", "top 85%"),
        });
      });
      return () => mm.revert();
    },
    { scope: pageRef }
  );

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Enterprise AI Solutions | Analytics Intelligence Ltd — Secure AI Infrastructure &amp; Automation</title>
        <link rel="canonical" href="https://analyticsintelligence.com/solutions/enterprises" />
        <meta
          name="description"
          content="Analytics Intelligence Ltd transforms enterprises with secure AI infrastructure — LLM-powered insights, automation, contextual reasoning, and data governance for organisations across Africa, Europe, and globally."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence, Analytics Intelligence Ltd, Analytics Intelligence AI, enterprise AI solutions, enterprise analytics platform, artificial intelligence solutions, business intelligence solutions, AI infrastructure, cloud AI platform, secure AI infrastructure, LLM-powered automation, data governance, compliant AI, contextual reasoning, data analytics consulting, predictive analytics for financial services, AI company in Mauritius, data analytics company in Africa, banking AI SSA"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta property="og:title" content="Enterprise AI Solutions | Analytics Intelligence Ltd" />
        <meta property="og:description" content="Secure AI infrastructure and LLM-powered automation for enterprises — data governance, contextual reasoning, and decision intelligence by Analytics Intelligence Ltd." />
        <meta property="og:url" content="https://analyticsintelligence.com/solutions/enterprises" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://analyticsintelligence.com/favicon.png" />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta name="twitter:title" content="Enterprise AI Solutions | Analytics Intelligence Ltd" />
        <meta name="twitter:description" content="Secure AI infrastructure and LLM-powered automation for enterprises — data governance, contextual reasoning, and decision intelligence by Analytics Intelligence Ltd." />
        <meta name="twitter:image" content="https://analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Enterprise AI Solutions",
            "serviceType": "Enterprise AI Infrastructure & Automation",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            },
            "description": "Analytics Intelligence Ltd delivers secure AI infrastructure, LLM-powered automation, and enterprise analytics for organisations across Africa, Europe, and globally.",
            "areaServed": ["Mauritius", "Africa", "United Kingdom", "Europe", "North America", "South America", "Australia"],
            "url": "https://analyticsintelligence.com/solutions/enterprises"
          }
        `}</script>
      </Helmet>

      <main ref={pageRef}>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center"
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16  pt-[126px] md:pt-[96px] py-[96px]">
            <div className="flex basis-3/5 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-4 flex flex-col items-center md:items-start">
                <span className="em-hero-item text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  Enterprise
                </span>
                <h2
                  className="em-hero-heading text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white"
                  dangerouslySetInnerHTML={{
                    __html:
                      "Secure AI Infrastructure for Enterprise-Grade Analytics",
                  }}
                />
                <p className="em-hero-item text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                  Empower your organisation with secure data pipelines that
                  unify behavioural and events data, enabling LLM-driven
                  insights, automation, and agentic decision support across the
                  enterprise.
                </p>
              </div>
            </div>
            <div className="basis-[40%] flex justify-end">
              <img
                className="em-hero-media w-full"
                src={Enterprises_Hero}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section
          className="bg-[#010127] relative flex flex-col space-y-[96px] pb-[96px]"
          id="use-cases"
        >
          <div className="w-[150px] lg:w-[400px] h-[200px] lg:h-[400px] bg-[#0073FF]/70 blur-[150px] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"></div>
          <div className="em-what-row container flex flex-col-reverse md:flex-row items-center mt-11 gap-8 md:gap-4 lg:gap-2 xl:gap-0 z-[100]">
            <div className="flex-1 basis-2/4 flex items-center justify-center">
              <img
                src={Enterprises_Deliver}
                alt="Data Structures"
                className="em-what-media w-full md:max-w-[400px] h-auto"
              />
            </div>

            <div className="em-what-copy flex-1 basis-2/4 space-y-4">
              <h2 class="text-2xl md:text-[32px] font-extrabold text-white text-center md:text-left">
                What is our Enterprise Solution?
              </h2>

              <div class="space-y-2 mb-12">
                <p className="text-sm md:text-base text-white">
                  Our enterprise solution provides a secure data infrastructure
                  for collecting, processing, and operationalising enterprise
                  datasets for LLM applications.
                </p>

                <p className="text-sm md:text-base text-white">
                  This solution combines:
                </p>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">Pulse:</span>
                    <span class="text-white ml-1">
                      Contextual behavioural tracking for web, mobile, and chat
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Secure Data Pipelines:
                    </span>
                    <span class="text-white ml-1">
                      Ingestion, transformation, governance and compliance
                      controls
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">MCP Server:</span>
                    <span class="text-white ml-1">
                      Contextual enrichment layer to strengthen LLM reasoning
                      and tool-calling
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">PLM: </span>
                    <span class="text-white ml-1">
                      Enterprise-tuned private language models deployed within
                      our secure cloud
                    </span>
                  </div>
                </div>
                <p className="text-sm md:text-base text-white">
                  Either together or individually, this foundation enables
                  enterprises to operationalise AI with confidence, control, and
                  end-to-end security.
                </p>
              </div>
            </div>
          </div>

          <div
            className="em-why-row container flex flex-col-reverse md:flex-row-reverse items-center mt-11 gap-8"
            style={{ zIndex: 100 }}
          >
            <div className="flex-1 basis-2/4 flex items-center justify-center">
              <img
                src={Enterprises_Build}
                alt="Data Structures"
                className="em-why-media w-full md:max-w-[400px] h-auto"
              />
            </div>

            <div className="em-why-copy flex-1 basis-2/4 space-y-4">
              <h2 class="text-2xl md:text-[32px] font-extrabold text-white text-center md:text-left">
                Why Enterprises Choose Us
              </h2>

              <div class="space-y-2 mb-12">
                <p className="text-sm md:text-base text-white">
                  Enterprises require AI that is explainable, compliant, secure,
                  and grounded in their actual business data not generic
                  internet data.
                </p>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Secure by Design:
                    </span>
                    <span class="text-white ml-1">
                      No data is exposed outside controlled and compliant
                      infrastructure
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Contextual Intelligence:
                    </span>
                    <span class="text-white ml-1">
                      LLMs reason using structured, enriched enterprise signals
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Enterprise Grade:
                    </span>
                    <span class="text-white ml-1">
                      Designed for CIO/CTO level governance, auditability and
                      scale
                    </span>
                  </div>
                </div>

                <div class="flex items-start text-sm md:text-base gap-3">
                  <span class="text-[#88BEFF]">•</span>
                  <div>
                    <span class="font-bold text-[#88BEFF]">
                      Outcome Focused:
                    </span>
                    <span class="text-white ml-1">
                      enabling faster analytics cycles, reduced decision
                      friction and measurable performance lift
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#EDF1FF] py-[48px]">
          <div className="container space-y-12">
            <div className="em-value-head">
              <h2 className="text-[#020128] font-extrabold text-[24px] md:text-[32px] text-center">
                Horizontal Value Across Industries
              </h2>
              <p className="text-[#020128] text-sm md:text-base text-center">
                No matter the sector, our solutions deliver
              </p>
            </div>

            <div className="em-value-grid flex justify-center flex-wrap gap-11">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="em-value-card group w-full lg:w-[45%] xl:max-w-[300px] bg-white hover:bg-poppingBlue flex flex-col items-center rounded-xl px-3 py-6 gap-4 transition-colors duration-700"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16 transition-all duration-700 group-hover:brightness-0 group-hover:invert"
                  />
                  <div className="space-y-1">
                    <h5 className="text-center text-base md:text-lg font-bold text-[#020128] transition-colors duration-700 group-hover:text-white">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128] text-center transition-colors duration-700 group-hover:text-white">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="bg-[#010127] relative py-[116px]"
          style={{
            backgroundImage: `url("${Our_Product_Bg}")`,
            backgroundPosition: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="em-cta container flex flex-col items-center gap-6">
            <div className="">
              <h2 className="text-[32px] md:text-[44px] text-center font-extrabold text-white">
                Built for Data-Driven Teams
              </h2>
              <p className="text-sm md:text-base text-center text-[#FFFFFF]">
                Analytics leaders use us to deliver more with the team they
                have, faster, safer and with higher confidence. 
              </p>
            </div>

            <Link to="/contact">
              <button className="text-sm md:text-base font-semibold py-3 w-[170px] text-center btn-sweep btn-sweep-blue text-white rounded-[100px] gap-2">
                Talk to an Expert
              </button>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}
